"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqItem {
  numeral: string;
  question: string;
  answer: string;
}
interface FaqCategory {
  category: string;
  items: FaqItem[];
}
const faqCategories: FaqCategory[] = [{
  category: "Network Infrastructure",
  items: [{
    numeral: "I.",
    question: "What defines your network architecture?",
    answer: "We deploy high-speed fiber and copper backbones designed for maximum throughput. Our systems utilize redundant ISP failover protocols and optional 5G/4G/LTE backups to ensure your business remains online regardless of external service interruptions."
  }, {
    numeral: "II.",
    question: "How do you handle site-to-site connectivity?",
    answer: "We implement high-performance wireless bridges for seamless building-to-building connectivity. This allows for unified network management across multiple physical locations without the need for expensive trenching or cabling."
  }]
}, {
  category: "Security & Surveillance",
  items: [{
    numeral: "III.",
    question: "What AI capabilities are included?",
    answer: "Our surveillance systems feature advanced AI-powered analytics, including facial recognition, license plate detection, and vehicle identification. These alerts are pushed directly to your mobile devices for real-time situational awareness."
  }, {
    numeral: "IV.",
    question: "Is the video storage scalable?",
    answer: "Yes, our storage solutions are fully expandable. We provide web-based monitoring interfaces that allow you to view multiple camera feeds on remote displays, with mobile app support for secure, high-definition playback."
  }]
}, {
  category: "Access & VoIP",
  items: [{
    numeral: "V.",
    question: "How does the access control work?",
    answer: "We utilize NFC-based authentication, supporting physical cards, tags, and mobile devices via Apple Passkey. Our systems integrate directly with video-enabled door readers to allow for automated entry based on visual recognition."
  }, {
    numeral: "VI.",
    question: "Are your VoIP systems customizable?",
    answer: "Our VoIP solutions are feature-rich and tailored to your specific workflow. We provide large-screen hardware that integrates video calling and camera feeds, ensuring internal communication is as efficient as possible."
  }]
}];
export default function Faq() {
  const [openItem, setOpenItem] = useState<string | null>(null);
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const accordion = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const closing = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const toggleItem = (key: string) => {
    setOpenItem(prev => prev === key ? null : key);
  };
  return <section data-section-id="3029" id="common-questions" className="bg-background text-foreground py-24 md:py-32 lg:py-[180px] px-6 md:px-10">
      <div className="max-w-[1200px] mx-auto">
        <div ref={intro.ref} className={`text-center mb-20 md:mb-28 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="mx-auto mb-8 text-primary text-2xl" aria-hidden="true" style={{
          fontFamily: "var(--font-serif)"
        }}>
            &#10038;
          </div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary mb-10">
            &mdash; Technical Inquiries
          </p>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[72px] leading-[1.05] text-foreground mb-10">
            Systems,
            <br />
            engineered
            <br />
            <span className="italic text-primary">for performance.</span>
          </h2>
          <p className="font-default text-base leading-[1.8] text-muted-foreground max-w-[580px] mx-auto">
            Common questions regarding our unified IT infrastructure, security protocols, and integration capabilities. For specific technical requirements, our engineering team is available for consultation.
          </p>
        </div>

        <div ref={accordion.ref} className={`max-w-[880px] mx-auto transition-all duration-1000 ease-out delay-150 ${accordion.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {faqCategories.map((cat, ci) => <div data-index={ci} key={cat.category} className="mb-14 last:mb-0">
              <h3 className="font-default italic text-primary text-sm md:text-base tracking-[0.15em] uppercase mb-4 pl-2" style={{
            letterSpacing: "0.2em"
          }}>
                {cat.category}
              </h3>

              <div>
                {cat.items.map((item, ii) => {
              const key = `${ci}-${ii}`;
              const isOpen = openItem === key;
              return <div data-index={ii} key={key} className="border-t border-border last:border-b last:border-b-border">
                      <Button onClick={() => toggleItem(key)} variant="ghost" className="group w-full justify-start gap-6 md:gap-8 py-7 md:py-8 h-auto px-0 hover:bg-transparent">
                        <span className="font-default italic text-primary text-base md:text-lg pt-1 w-8 shrink-0" aria-hidden="true">
                          {item.numeral}
                        </span>
                        <span className={`flex-1 text-left font-default font-semibold text-foreground text-xl md:text-2xl leading-[1.35] transition-all duration-300 ${isOpen ? "italic" : "group-hover:italic"}`}>
                          {item.question}
                        </span>
                        <span className="shrink-0 pt-1 text-primary" aria-hidden="true">
                          <Plus className={`size-7 md:size-8 transition-transform duration-[400ms] ease-out ${isOpen ? "rotate-45" : "rotate-0"}`} strokeWidth={1} />
                        </span>
                      </Button>

                      <div className={`grid transition-all duration-500 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                        <div className="overflow-hidden">
                          <div className="pl-14 md:pl-16 pr-8 md:pr-16 pb-8">
                            <p className="font-default text-base text-muted-foreground leading-[1.8]">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </div>)}
        </div>

        <div ref={closing.ref} className={`text-center mt-24 md:mt-32 transition-all duration-1000 ease-out delay-200 ${closing.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default italic text-foreground text-[22px] md:text-[26px] leading-snug mb-8">
            Need a custom solution?
          </p>
          <Button asChild variant="link" className="font-mono text-xs tracking-[0.3em] uppercase text-primary hover:text-primary/80 px-0 h-auto">
            <Link to="/SolutionsPage">
              Contact Our Engineering Team &rarr;
            </Link>
          </Button>
          <div className="mt-10 flex justify-center">
            <span className="block w-px h-16 bg-border" aria-hidden="true"></span>
          </div>
        </div>
      </div>
    </section>;
}